import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Box, Typography, Stack, IconButton, Menu, MenuItem, Divider, Tooltip,
} from "@mui/material";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import { useAuth } from "../auth/AuthContext.js";
import { useColorMode } from "../theme/ColorModeContext.js";
import { billingApi } from "../api/billing.js";
import { BRAND, DIM } from "../theme/index.js";
import { useTheme } from "@mui/material";

const MAIN_NAV = [
  { label: "Overview",    to: "/dashboard",             icon: <DashboardOutlinedIcon   sx={{ fontSize: 16 }} />, end: true },
  { label: "Bots",        to: "/dashboard/bots",        icon: <SmartToyOutlinedIcon    sx={{ fontSize: 16 }} /> },
  { label: "Submissions", to: "/dashboard/submissions", icon: <InboxOutlinedIcon       sx={{ fontSize: 16 }} /> },
  { label: "Insights",    to: "/dashboard/insights",    icon: <InsightsOutlinedIcon    sx={{ fontSize: 16 }} /> },
];

const ACCOUNT_NAV = [
  { label: "Billing",  to: "/dashboard/billing",  icon: <CreditCardOutlinedIcon sx={{ fontSize: 16 }} /> },
  { label: "Settings", to: "/dashboard/settings", icon: <SettingsOutlinedIcon   sx={{ fontSize: 16 }} /> },
];

function emailToDisplayName(email: string): string {
  const local = email.split("@")[0];
  const parts = local.split(/[._+\-]/);
  if (parts.length >= 2) {
    const first = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
    const lastInitial = parts[1].charAt(0).toUpperCase();
    return `${first} ${lastInitial}.`;
  }
  return local.charAt(0).toUpperCase() + local.slice(1);
}

function emailToInitials(email: string): string {
  const local = email.split("@")[0];
  const parts = local.split(/[._+\-]/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return local.slice(0, 2).toUpperCase();
}

interface NavSectionProps {
  label: string;
  items: typeof MAIN_NAV;
}

function NavSection({ label, items }: NavSectionProps) {
  const theme = useTheme();

  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography
        variant="caption"
        sx={{
          display: "block",
          px: 1.5,
          pb: 0.75,
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: "0.8px",
          textTransform: "uppercase",
          color: theme.palette.text.disabled,
        }}
      >
        {label}
      </Typography>

      {items.map(item => (
        <NavLink key={item.to} to={item.to} end={item.end} style={{ textDecoration: "none" }}>
          {({ isActive }) => (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                px: 1.5,
                py: "7px",
                mx: 0.5,
                borderRadius: "6px",
                mb: 0.25,
                cursor: "pointer",
                backgroundColor: isActive ? DIM.cyan : "transparent",
                transition: "background-color 150ms ease",
                "&:hover": {
                  backgroundColor: isActive ? DIM.cyan : theme.palette.action.hover,
                },
              }}
            >
              <Box sx={{ color: isActive ? BRAND.cyan : theme.palette.text.secondary, display: "flex", flexShrink: 0 }}>
                {item.icon}
              </Box>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: isActive ? 600 : 500,
                  color: isActive ? BRAND.cyan : theme.palette.text.secondary,
                  lineHeight: 1,
                }}
              >
                {item.label}
              </Typography>
            </Box>
          )}
        </NavLink>
      ))}
    </Box>
  );
}

interface Props { width: number }

export function Sidebar({ width }: Props) {
  const theme = useTheme();
  const { user, logout } = useAuth();
  const { mode, toggle } = useColorMode();
  const nav = useNavigate();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const border = theme.palette.divider;

  const [planLabel, setPlanLabel] = useState<string>("…");

  useEffect(() => {
    billingApi.get()
      .then(info => {
        const t = info.tier ?? "free";
        if (t.startsWith("pro"))     setPlanLabel("Pro");
        else if (t.startsWith("starter")) setPlanLabel("Starter");
        else setPlanLabel("Free plan");
      })
      .catch(() => setPlanLabel("Free plan"));
  }, []);

  const displayName = user?.email ? emailToDisplayName(user.email) : "User";
  const initials    = user?.email ? emailToInitials(user.email) : "??";

  return (
    <Box sx={{
      position: "fixed", top: 0, left: 0, bottom: 0,
      width,
      bgcolor: "background.paper",
      borderRight: `1px solid ${border}`,
      display: "flex",
      flexDirection: "column",
      zIndex: 100,
    }}>
      {/* Logo */}
      <Box sx={{
        height: 64, px: 2.5,
        display: "flex", alignItems: "center",
        borderBottom: `1px solid ${border}`,
        flexShrink: 0,
      }}>
        <img
          src={mode === "dark" ? "/chatfab-logo-dark.png" : "/chatfab-logo.png"}
          alt="ChatFab"
          style={{ height: 26 }}
        />
      </Box>

      {/* Nav */}
      <Box sx={{ flex: 1, px: 1, pt: 2, overflowY: "auto" }}>
        <NavSection label="Main" items={MAIN_NAV} />
        <NavSection label="Account" items={ACCOUNT_NAV} />
      </Box>

      {/* User panel */}
      <Box sx={{ px: 1.5, pb: 2, pt: 1.5, borderTop: `1px solid ${border}`, flexShrink: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          {/* Avatar */}
          <Box
            sx={{
              width: 34, height: 34,
              borderRadius: "8px",
              background: "linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 11, fontWeight: 700, color: "#fff",
              fontFamily: "'DM Sans', sans-serif",
              flexShrink: 0,
              letterSpacing: "0.3px",
            }}
          >
            {initials}
          </Box>

          {/* Name + plan */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap sx={{ lineHeight: 1.3 }}>
              {displayName}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ lineHeight: 1.3 }}>
              {planLabel}
            </Typography>
          </Box>

          {/* Actions */}
          <Stack direction="row" spacing={0}>
            <Tooltip title={mode === "dark" ? "Light mode" : "Dark mode"}>
              <IconButton size="small" onClick={toggle} sx={{ opacity: 0.6, "&:hover": { opacity: 1 } }}>
                {mode === "dark"
                  ? <LightModeOutlinedIcon sx={{ fontSize: 15 }} />
                  : <DarkModeOutlinedIcon  sx={{ fontSize: 15 }} />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Sign out">
              <IconButton size="small" onClick={e => setAnchor(e.currentTarget)} sx={{ opacity: 0.6, "&:hover": { opacity: 1 } }}>
                <LogoutIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        <Menu
          anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" color="text.secondary">{user?.email}</Typography>
          </Box>
          <Divider />
          <MenuItem onClick={() => { logout(); nav("/login"); setAnchor(null); }}>
            <LogoutIcon fontSize="small" sx={{ mr: 1.5, color: "error.main" }} />
            <Typography variant="body2" color="error.main">Sign out</Typography>
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
}
